/**
 * API utility functions with authentication handling
 */

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001";

/**
 * Get authentication headers for API requests
 */
export function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem("token");
  console.log(
    "Auth token from localStorage:",
    token ? "Token exists" : "No token found"
  );

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
    console.log(
      "Added Authorization header:",
      `Bearer ${token.substring(0, 15)}...`
    );
  }

  return headers as HeadersInit;
}

/**
 * Basic fetch wrapper with authentication
 */
export async function apiRequest(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  body?: any
): Promise<any> {
  const url = `${API_BASE_URL}${endpoint}`;
  console.log(`Making ${method} request to ${url}`);
  const headers = getAuthHeaders();

  const options: RequestInit = {
    method,
    headers,
  };

  if (body && method !== "GET") {
    options.body = JSON.stringify(body);
  }

  console.log("Request options:", {
    method: options.method,
    hasAuthHeader: !!(headers as Record<string, string>)["Authorization"],
  });

  const response = await fetch(url, options);
  console.log(`Response from ${endpoint}:`, {
    status: response.status,
    ok: response.ok,
    statusText: response.statusText,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    console.error(`API Error (${response.status}):`, errorText);
    throw new Error(`API Error (${response.status}): ${errorText}`);
  }

  // Return null for 204 No Content
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

/**
 * Authentication API calls
 */
export const authApi = {
  login: async (email: string, password: string) => {
    return apiRequest("/auth/login", "POST", { email, password });
  },

  register: async (credentials: any) => {
    console.log("Registering user:", { email: credentials.email });
    return apiRequest("/auth/register", "POST", credentials);
  },

  googleAuth: async (googleData: any) => {
    return apiRequest("/auth/google", "POST", googleData);
  },

  getProfile: async () => {
    console.log("Fetching user profile");
    return apiRequest("/auth/profile");
  },

  updateProfile: async (updates: any) => {
    return apiRequest("/auth/profile", "PUT", updates);
  },
};

/**
 * Targets API calls
 */
export const targetsApi = {
  getAll: async () => {
    console.log("Fetching all targets");
    return apiRequest("/targets");
  },

  getById: async (id: string) => {
    return apiRequest(`/targets/${id}`);
  },

  create: async (targetData: any) => {
    return apiRequest("/targets", "POST", targetData);
  },

  update: async (id: string, updates: any) => {
    return apiRequest(`/targets/${id}`, "PUT", updates);
  },

  delete: async (id: string) => {
    return apiRequest(`/targets/${id}`, "DELETE");
  },

  import: async (targets: any[]) => {
    // Process each target individually to avoid payload size issues
    console.log(`Importing ${targets.length} targets individually`);
    return Promise.all(
      targets.map((target) => apiRequest("/targets", "POST", target))
    );
  },

  // Batch operations
  batchDelete: async (ids: string[]) => {
    // Execute all delete operations in parallel
    return Promise.all(ids.map((id) => apiRequest(`/targets/${id}`, "DELETE")));
  },

  batchUpdate: async (updates: Array<{ id: string; data: any }>) => {
    // Execute all update operations in parallel
    return Promise.all(
      updates.map(({ id, data }) => apiRequest(`/targets/${id}`, "PUT", data))
    );
  },
};

/**
 * Filter Settings API calls
 */
export const filterSettingsApi = {
  getAll: async () => {
    console.log("Fetching filter settings");
    return apiRequest("/filter-settings");
  },

  save: async (settings: any[]) => {
    return apiRequest("/filter-settings", "POST", settings);
  },
};
